import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useJourney } from "@/contexts/JourneyContext";
import FullScreenLoader from "./FullScreenLoader";
import { DIGIO_DIGILOCKER_BASE_URL } from "@/config";

// ---------------------------------------------------------------------------
// Legacy stage→route map (V2 / LenderBAPJourney system)
// Kept for backward compatibility with users who have no journey_config_id.
// New users on the V3 builder_app engine get their route from the API.
// ---------------------------------------------------------------------------
const LEGACY_STAGE_ROUTE_MAP: Record<string, string> = {
  OTP_LOGIN: "/",
  WELCOME: "/welcome",
  REQUIREMENTS: "/requirements",
  PERSONAL_INFO: "/personal-info",
  LOAN_AMOUNT_WHITESPACE: "/loan-amount",
  SUCCESS_WHITESPACE: "/success-whitespace",
  REJECTED_WHITESPACE: "/rejected-whitespace",
  PRE_APPROVED_AMOUNT_WHITESPACE: "/whitespace/pre-approved-amount",
  CURATED_OFFERS_WHITESPACE: "/whitespace/curating-offers",
  BUSINESS_INFO: "/business-info",
  ADDITIONAL_INFO: "/additional-details",
  TRIGGER_VERIFICATION_SET_A: "/ruleset-a",
  ALL_OFFERS: "/all-offers",
  LOAN_OFFER: "/loan-offer",
  LOAN_AMOUNT_CONFIRMATION: "/loan-amount-confirmation",
  FINAL_OFFER: "/final-offer",
  SELFIE_PHOTO_AND_LOCATION: "/selfie-photo-loc-verification",
  SHOP_ONLY_PHOTO_AND_LOCATION: "/shop-only-photo-loc-verification",
  SHOP_SELFIE_PHOTO_AND_LOCATION: "/shop-selfie-photo-loc-verification",
  SHOP_VIDEO_AND_LOCATION: "/shop-video-loc-verification",
  DIGI_CHECK: "/digi-check",
  MAS_DIGIO_JOURNEY: "/mas-digio-journey",
  DIGIO_DIGILOCKER_CALLBACK: "/kyc-digio-digilocker/callback",
  SELFIE_AADHAAR_MATCH: "/selfie-aadhaar-match",
  BANK_ACCOUNT_AUTH: "/bank-account-authentication",
  ENACH_MANDATE: "/setup-mandates",
  UPI_MANDATE: "/setup-mandates",
  TRIGGER_VERIFICATION_SET_B: "/ruleset-b",
  TRIGGER_VERIFICATION_SET_WHITESPACE: "/ruleset-whitespace",
  FINAL_KFS: "/final-kfs",
  VERIFICATION_PENDING: "/waiting-for-updates",
  DIGIO_ESIGN: "/esign",
  DOC_PUSH: "/doc-push",
  COMPLETED: "/loan-success",
  LOAN_APPLICATION_UNABLE: "/loan-application-unable",
  JOURNEY_ERROR: "/journey-error",
  REJECTED: "/final-reject-state",
};

const LEGACY_ROUTE_STAGE_MAP = Object.fromEntries(
  Object.entries(LEGACY_STAGE_ROUTE_MAP).map(([stage, route]) => [route, stage])
);

// ---------------------------------------------------------------------------
// V3 dynamic page state
// ---------------------------------------------------------------------------
interface DynamicPageInfo {
  id: number;
  slug: string;
  route: string;
  step_index: number;
  total_steps: number;
  journey_config_id: number;
}

async function fetchCurrentPage(): Promise<DynamicPageInfo | null> {
  try {
    const res = await fetch("/api/journey/current-page/", {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (res.status === 204 || res.status === 404) return null;
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * JourneySync Component
 *
 * V3 behaviour:
 *   - If the user has a journey_config_id (new engine), fetches the current
 *     page from /api/journey/current-page/ and uses its `route` field.
 *   - Falls back to the legacy stageRouteMap for users on the old engine.
 *
 * Key Behaviour (unchanged):
 *   - Shows full-screen loader while validating
 *   - Compares current URL with expected route
 *   - If URLs don't match: loader stays up and redirects to expected route
 *   - Only allows UI rendering when URL matches expected route
 */
const JourneySync = () => {
  const { user } = useAuth();
  const { state, loading, logEvent, refresh: refreshJourney } = useJourney();
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [hasValidated, setHasValidated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastMismatchPath, setLastMismatchPath] = useState<string | null>(null);

  // V3: dynamic page info fetched from builder_app engine
  const [dynamicPage, setDynamicPage] = useState<DynamicPageInfo | null>(null);
  const [dynamicPageLoading, setDynamicPageLoading] = useState(false);

  // Determine if user is on V3 engine (has journey_config_id)
  const isV3User = Boolean(state && (state as any).journey_config_id);

  // ---------------------------------------------------------------------------
  // V3: fetch dynamic page when user is on new engine
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user || !isV3User) return;

    setDynamicPageLoading(true);
    fetchCurrentPage()
      .then((page) => {
        setDynamicPage(page);
      })
      .finally(() => {
        setDynamicPageLoading(false);
      });
  }, [user, isV3User, state]);

  // ---------------------------------------------------------------------------
  // Legacy helpers (used when isV3User === false)
  // ---------------------------------------------------------------------------
  const getCurrentCheckpointStages = (): string[] => {
    if (!state?.checkpoint_template || state.checkpoint_template.length === 0) {
      return state?.current_stage ? [state.current_stage] : [];
    }
    if (
      state.current_checkpoint_index >= 0 &&
      state.current_checkpoint_index < state.checkpoint_template.length
    ) {
      return state.checkpoint_template[state.current_checkpoint_index] || [];
    }
    return state?.current_checkpoint || [];
  };

  const isStageInCurrentCheckpoint = (stage: string): boolean => {
    return getCurrentCheckpointStages().includes(stage);
  };

  const getCurrentCheckpointRoute = (): string | null => {
    const checkpointStages = getCurrentCheckpointStages();
    if (checkpointStages.length === 0) return null;

    if (state?.current_stage && checkpointStages.includes(state.current_stage)) {
      return LEGACY_STAGE_ROUTE_MAP[state.current_stage] || null;
    }

    const firstStage = checkpointStages[0];
    return LEGACY_STAGE_ROUTE_MAP[firstStage] || null;
  };

  // ---------------------------------------------------------------------------
  // Main validation effect
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const publicRoutes = [
      "/",
      "/session-timeout",
      "/terms",
      "/privacy",
      "/index",
      "/final-reject-state",
      "/admin-login",
      "/lender-rejection-wait",
    ];
    const isAdminRoute = location.pathname.startsWith("/admin-");
    const isPublicRoute =
      publicRoutes.includes(location.pathname) ||
      location.pathname.startsWith("/consent/") ||
      location.pathname.startsWith("/kfs/") ||
      location.pathname.startsWith("/journey/");  // V3 dynamic pages

    if (isPublicRoute || isAdminRoute || user?.is_staff || user?.is_superuser) {
      setIsValidating(false);
      setHasValidated(true);
      return;
    }

    if (!user) {
      setIsValidating(false);
      setHasValidated(false);
      return;
    }

    if (loading || !state) {
      setIsValidating(true);
      setHasValidated(false);
      return;
    }

    // -------------------------------------------------------------------------
    // V3 path: use dynamic page route from builder_app engine
    // -------------------------------------------------------------------------
    if (isV3User) {
      if (dynamicPageLoading) {
        setIsValidating(true);
        setHasValidated(false);
        return;
      }

      if (!dynamicPage) {
        // Journey complete or no page — allow current route
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      const expectedRoute = dynamicPage.route;

      if (location.pathname === expectedRoute) {
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      // Allow /journey/:slug as an alternative rendering path for dynamic pages
      const dynamicJourneyPath = `/journey/${dynamicPage.slug}`;
      if (location.pathname === dynamicJourneyPath) {
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      if (retryCount >= 2) {
        console.warn(`JourneySync (V3): Mismatch. Redirecting to ${expectedRoute}`);
        navigate(expectedRoute, { replace: true });
        setIsValidating(true);
        setHasValidated(false);
        return;
      }

      setRetryCount((prev) => prev + 1);
      refreshJourney().catch((e) => {
        console.error("JourneySync (V3): Failed to refresh state", e);
      });
      setIsValidating(true);
      setHasValidated(false);
      return;
    }

    // -------------------------------------------------------------------------
    // Legacy path: use stageRouteMap
    // -------------------------------------------------------------------------
    let expectedRoute: string | null = null;

    if (state.checkpoint_template && state.checkpoint_template.length > 0) {
      expectedRoute = getCurrentCheckpointRoute();
      if (!expectedRoute) {
        expectedRoute = LEGACY_STAGE_ROUTE_MAP[state.current_stage] || null;
      }
    } else {
      expectedRoute = LEGACY_STAGE_ROUTE_MAP[state.current_stage] || null;
    }

    if (!expectedRoute) {
      console.warn("No expected route found for journey state", state);
      setIsValidating(false);
      setHasValidated(true);
      return;
    }

    if (location.pathname !== lastMismatchPath) {
      setLastMismatchPath(location.pathname);
      setRetryCount(0);
    }

    if (location.pathname !== expectedRoute) {
      const currentRouteStage = LEGACY_ROUTE_STAGE_MAP[location.pathname];
      const isRouteInCheckpoint =
        currentRouteStage && isStageInCurrentCheckpoint(currentRouteStage);

      if (
        location.pathname === "/requirements" &&
        state.current_stage === "WELCOME"
      ) {
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      if (
        (currentRouteStage && currentRouteStage === state.current_stage) ||
        isRouteInCheckpoint
      ) {
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      if (
        currentRouteStage &&
        state.checkpoint_template &&
        state.checkpoint_template.length > 0
      ) {
        const currentCheckpointIdx = state.current_checkpoint_index;
        if (currentCheckpointIdx + 1 < state.checkpoint_template.length) {
          const nextCheckpoint =
            state.checkpoint_template[currentCheckpointIdx + 1];
          if (nextCheckpoint && nextCheckpoint.includes(currentRouteStage)) {
            setIsValidating(false);
            setHasValidated(true);
            return;
          }
        }
      }

      if (retryCount >= 2) {
        console.warn(
          `JourneySync: Mismatch persisted after ${retryCount} retries. Redirecting to ${expectedRoute}`
        );
        navigate(expectedRoute, { replace: true });
        setIsValidating(true);
        setHasValidated(false);
        return;
      }

      setRetryCount((prev) => prev + 1);
      console.log(`JourneySync: Retrying state sync (${retryCount + 1}/3)...`);

      refreshJourney().catch((e) => {
        console.error("JourneySync: Failed to refresh state", e);
      });

      setIsValidating(true);
      setHasValidated(false);
      return;
    }

    setIsValidating(false);
    setHasValidated(true);
  }, [
    user,
    loading,
    state,
    location.pathname,
    navigate,
    refreshJourney,
    isV3User,
    dynamicPage,
    dynamicPageLoading,
  ]);

  // ---------------------------------------------------------------------------
  // Log page view event (only after validation passes)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user || !state || !hasValidated || isValidating) return;

    const derivedStage = isV3User
      ? dynamicPage?.slug || location.pathname
      : LEGACY_ROUTE_STAGE_MAP[location.pathname] || state.current_stage;

    logEvent({
      stage: derivedStage,
      route: location.pathname,
      event_type: "page_view",
      payload: { pathname: location.pathname },
    });
  }, [user, state, location.pathname, logEvent, isValidating, hasValidated]);

  return (
    <FullScreenLoader loading={loading || isValidating || dynamicPageLoading} />
  );
};

export default JourneySync;
